import { RESERVATION_API_BASE_URL, SITE_API_BASE_URL } from '@m/config/env';
import { KeycloakService } from '@m/keycloak-service/KeycloakService';
import { stringify } from 'query-string';

import { getAuthToken, getDefaultAuthMethod } from './apiUtils';

import type { APIResponse, FetchConfig, FetchOptions } from './types';

export class FetchError extends Error {
  status: number;
  statusText: string;

  constructor(message: string, status: number, statusText: string) {
    super(message);
    this.name = 'FetchError';
    this.status = status;
    this.statusText = statusText;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, FetchError);
    }
  }
}

type ApiFetchOptions = FetchOptions & { baseUrl?: string; isPolling?: boolean };
type NonApiResponseFetchOptions = ApiFetchOptions & {
  /** Skip sending Authorization header (for public endpoints) */
  skipAuth?: boolean;
};

/**
 * @deprecated Use apiFetchV2 with tanstack query instead.
 */
export async function apiFetch<ResponseData = any>(
  endpoint: string,
  {
    method = 'GET',
    headers = {},
    params,
    body,
    stringifyOpts,
    sendRawBody,
    signal,
    statusCodes = false,
    isPolling = false,
    baseUrl = SITE_API_BASE_URL,
  }: ApiFetchOptions = {},
  { authMethod = getDefaultAuthMethod() }: FetchConfig = {},
): Promise<APIResponse<ResponseData>> {
  if (isPolling && document.visibilityState !== 'visible') {
    // Browser tab is not active. Skipping the request.
    return { success: false };
  }

  const shouldStringifyBody = !sendRawBody;

  const fetchOptions: RequestInit & { headers: Record<string, string> } = {
    method,
    headers,
    signal,
  };

  if (shouldStringifyBody) {
    fetchOptions.headers['Content-Type'] = 'application/json';
  }

  if (KeycloakService.isLoggedIn()) {
    const authToken = await KeycloakService.ensureFreshToken();
    if (authToken) {
      fetchOptions.headers.Authorization = `Bearer ${authToken}`;
    }
  } else if (authMethod === 'token') {
    const authToken = await getAuthToken();
    if (authToken) {
      fetchOptions.headers.Authorization = authToken;
    }
  } else if (authMethod === 'cookie') {
    fetchOptions.credentials = 'include';
  }
  if (body && shouldStringifyBody) {
    // Format body as JSON
    fetchOptions.body = JSON.stringify(body);
  } else if (body) {
    // Send body without any modification
    fetchOptions.body = body;
  }

  let query = '';
  if (params && typeof params === 'object' && Object.keys(params).length > 0) {
    query = `?${stringify(params, stringifyOpts)}`;
  }
  const reqUrl = `${baseUrl}${endpoint}${query}`;

  return fetch(reqUrl, fetchOptions)
    .then(async (response) => {
      if (statusCodes) {
        return response.json();
      }
      if (response.status === 200) {
        return response.json();
      }
      const data = await response.json().catch((e: any) => {
        // if response isn't a json, then we return no data
        // idk if we want to display 'Gateway Timeout' or 'Unexpected Error' to the client, so setting the error to undefined
        // eslint-disable-next-line no-console
        console.log('e', e);
        return undefined;
      });

      return {
        success: false,
        status: response.status,
        statusText: response.statusText,
        ...(typeof data === 'object' ? data : { error: data }),
      };
    })
    .catch((e) => {
      if (e instanceof Error && e.name === 'AbortError') {
        throw e;
      }
      // eslint-disable-next-line no-console
      console.log(e);
      if (statusCodes) {
        throw e;
      }
      return { success: false };
    });
}

export async function apiFetchV2<ResponseData = any>(
  endpoint: string,
  options: ApiFetchOptions = {},
  config: FetchConfig = {},
) {
  const response = await apiFetch<ResponseData>(endpoint, options, config);

  if (!response.success) {
    throw new Error(response.error || response.statusText);
  }

  // If the response is successful, we can safely cast the data to the type of the response
  return response.data!;
}

export async function reservationApiFetch<ResponseData = any>(
  endpoint: string,
  options: ApiFetchOptions = {},
  config: FetchConfig = {},
) {
  return nonApiResponseFetch<ResponseData>(
    endpoint,
    {
      ...options,
      baseUrl: RESERVATION_API_BASE_URL,
    },
    config,
  );
}

/** Prefer gateway `Error.Message` (top-level or nested under `*RS`) when present. */
export function getApiErrorMessageFromBody(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const record = data as Record<string, unknown>;
  const topLevel = record.Error as { Message?: string } | undefined;
  if (typeof topLevel?.Message === 'string' && topLevel.Message.trim()) {
    return topLevel.Message;
  }
  for (const value of Object.values(record)) {
    if (!value || typeof value !== 'object') continue;
    const nested = (value as Record<string, unknown>).Error as { Message?: string } | undefined;
    if (typeof nested?.Message === 'string' && nested.Message.trim()) {
      return nested.Message;
    }
  }
  return undefined;
}

export async function nonApiResponseFetch<ResponseData = any>(
  endpoint: string,
  options: NonApiResponseFetchOptions = {},
  config: FetchConfig = {},
): Promise<ResponseData> {
  const {
    method = 'GET',
    headers = {},
    params,
    body,
    stringifyOpts,
    sendRawBody,
    baseUrl = SITE_API_BASE_URL,
    skipAuth = false,
    signal,
  } = options;

  const shouldStringifyBody = !sendRawBody;

  const fetchOptions: RequestInit & { headers: Record<string, string> } = {
    method,
    headers,
    signal,
  };

  if (shouldStringifyBody) {
    fetchOptions.headers['Content-Type'] = 'application/json';
  }

  const { authMethod = getDefaultAuthMethod() } = config;

  if (!skipAuth) {
    if (KeycloakService.isLoggedIn()) {
      const authToken = await KeycloakService.ensureFreshToken();
      if (authToken) {
        fetchOptions.headers.Authorization = `Bearer ${authToken}`;
      }
    } else if (authMethod === 'token') {
      const authToken = await getAuthToken();
      if (authToken) {
        fetchOptions.headers.Authorization = authToken;
      }
    } else if (authMethod === 'cookie') {
      fetchOptions.credentials = 'include';
    }
  }

  if (body && shouldStringifyBody) {
    fetchOptions.body = JSON.stringify(body);
  } else if (body) {
    fetchOptions.body = body;
  }

  let query = '';
  if (params && typeof params === 'object' && Object.keys(params).length > 0) {
    query = `?${stringify(params, stringifyOpts)}`;
  }
  const reqUrl = `${baseUrl}${endpoint}${query}`;

  const response = await fetch(reqUrl, fetchOptions);

  if (!response.ok) {
    // Tolerate malformed error bodies so we can still throw with statusText.
    // Successful responses keep a strict parse so contract violations surface.
    const data: unknown = await response.json().catch(() => undefined);
    throw new FetchError(
      getApiErrorMessageFromBody(data) || `Failed to fetch: ${response.statusText}`,
      response.status,
      response.statusText,
    );
  }

  return (await response.json()) as ResponseData;
}
